import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { AuthenticationProvider } from '@zenbusiness/application-commons-authentication';
import { useEnv } from '@zenbusiness/application-commons-env';
import { ExperimentsProvider } from '@zenbusiness/application-commons-experiments';
import { GraphQLProvider } from '@zenbusiness/application-commons-graphql';
import { IdentityProvider } from '@zenbusiness/application-commons-identity';
import { IntlProvider } from '@zenbusiness/application-commons-intl';
import { LoadingZone, SuspenseZone } from '@zenbusiness/application-commons-loading';
import { LoggingProvider, ErrorBoundary } from '@zenbusiness/application-commons-logging';
import { VisitProvider } from '@zenbusiness/application-commons-visit';
import { WebView } from '@zenbusiness/application-commons-webview';
import PageError from '@zenbusiness/zui/PageError';
import { ZuiThemeProvider } from '@zenbusiness/zui/styles';

import { DEFAULT_SEGMENT, NAME, RELEASE_VERSION } from '#client/build-env';
import { intlFormats } from '#client/constants';
import { CustomFormProvider } from '#client/contexts/CustomFormContext';

import { ChatBotWrapper } from './ChatBotWrapper';
import ClickstreamWrapper from './ClickstreamWrapper';
import globalGraphQLErrorHandler from './GraphQLErrorHandler';

const emotionCache = createCache({
  key: NAME
});

// eslint-disable-next-line react/prop-types
export default function Providers({ children, federated }) {
  const {
    AUTH0_AUDIENCE,
    AUTH0_CLIENTID,
    AUTH0_CONNECTION,
    AUTH0_DOMAIN,
    GRAPHQL_ROUTE,
    AUTH_PATHS,
    LOCAL_LOGIN_APP_ORIGIN,
    OIDC_CLIENT_ID,
    OIDC_LOGIN_ENABLED,
    USE_ZB_SESSION_INFO,
    NODE_ENV,
    DEPLOY_ENV
  } = useEnv('head-first'); // use MFE values
  const { LD_CLIENT_ID, LOCALE } = useEnv('root-first'); // use parent values

  return (
    <LoadingZone fallback={<div />} loadId="App/Providers">
      <LoggingProvider
        tag-frontend={NAME}
        tag-frontend-release={`${NAME}-${RELEASE_VERSION || 'unset'}`}
        tag-isWebView={String(WebView?.isWebView)}
      >
        <ZuiThemeProvider baseline={!federated} scopedBaseline={federated}>
          <ErrorBoundary
            fallback={<PageError>Provider Error</PageError>}
            id={`${NAME}/App/Providers`}
          >
            <CacheProvider value={emotionCache}>
              <SuspenseZone fallback={<div />} loadId={`${NAME}/App/Providers#Intl`}>
                <IntlProvider
                  defaultFormats={intlFormats}
                  defaultLocale="en-US"
                  findPath={`${DEFAULT_SEGMENT}/messages/<%= locale %>/${RELEASE_VERSION}`}
                  formats={intlFormats}
                  locale={LOCALE}
                >
                  <ExperimentsProvider appId={NAME} clientId={LD_CLIENT_ID} deployEnv={DEPLOY_ENV}>
                    <AuthenticationProvider
                      audience={AUTH0_AUDIENCE}
                      clientID={AUTH0_CLIENTID}
                      connection={AUTH0_CONNECTION}
                      domain={AUTH0_DOMAIN}
                      {...AUTH_PATHS}
                      autoLogoutOnSessionExpiration={false}
                      enableOidcLogin={OIDC_LOGIN_ENABLED}
                      isLocal={NODE_ENV === 'development'}
                      localLoginAppOrigin={LOCAL_LOGIN_APP_ORIGIN}
                      oidcClientId={OIDC_CLIENT_ID}
                      userType="customer"
                      useZbSessionInfo={USE_ZB_SESSION_INFO}
                    >
                      <GraphQLProvider url={GRAPHQL_ROUTE} onError={globalGraphQLErrorHandler}>
                        <VisitProvider disableCreation={federated}>
                          <IdentityProvider>
                            <ClickstreamWrapper>
                              <CustomFormProvider enablePersistence>
                                <ChatBotWrapper>{children}</ChatBotWrapper>
                              </CustomFormProvider>
                            </ClickstreamWrapper>
                          </IdentityProvider>
                        </VisitProvider>
                      </GraphQLProvider>
                    </AuthenticationProvider>
                  </ExperimentsProvider>
                </IntlProvider>
              </SuspenseZone>
            </CacheProvider>
          </ErrorBoundary>
        </ZuiThemeProvider>
      </LoggingProvider>
    </LoadingZone>
  );
}
